// Coquille du portail citoyen (écrans S20 et S21) : en-tête avec la collectivité choisie, les
// notifications (un point signale les non lues), la bascule jour et nuit et le compte ;
// navigation basse à quatre entrées (Dossiers, Démarches, Paiements, Messages avec le nombre de
// messages non lus) plus Menu ; bandeau hors connexion ; pied discret.
import { PREFERENCES_PAR_DEFAUT, type Preferences } from '@cc/contrats/ui-preferences';
import { Bandeau, IndicateurEnvironnement, lireStockage, ecrireStockage } from '@cc/ui';
import { onlineManager } from '@tanstack/react-query';
import {
  Bell,
  Building2,
  CalendarDays,
  CalendarRange,
  CreditCard,
  FileText,
  FolderOpen,
  Home,
  LogIn,
  LogOut,
  Megaphone,
  Menu,
  MessageSquare,
  MessageSquareWarning,
  Moon,
  Paperclip,
  ShieldCheck,
  Sun,
  UserRound,
  X,
} from 'lucide-react';
import { useEffect, useId, useState, useSyncExternalStore, type ComponentType } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router';
import { appliquerPreferences } from '../../preferences/FournisseurPreferences';
import { useNonLusMessages, useNotificationsUsager } from './api';
import { LIBELLES_TYPE_COLLECTIVITE, useCollectiviteChoisie, useCollectivitesPubliques } from './collectivite';
import { libelleNonLus } from './conversation';
import { FournisseurSessionUsager, useSessionUsager } from './session/FournisseurSessionUsager';
import styles from './citoyen.module.css';

const CLE_THEME = 'cc-preferences:dernieres';

function useEnLigne(): boolean {
  return useSyncExternalStore(
    (rappel) => onlineManager.subscribe(rappel),
    () => onlineManager.isOnline(),
    () => true,
  );
}

/** Thème jour ou nuit : mémorisé sur l'appareil (même clé que l'espace Administration), appliqué sur <html>. */
function BasculeTheme() {
  const [theme, setTheme] = useState<Preferences['theme']>(() => lireStockage<Preferences>(CLE_THEME)?.theme ?? 'systeme');
  const sombre = theme === 'nuit' || (theme !== 'jour' && typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches);
  const basculer = () => {
    const suivant: Preferences['theme'] = sombre ? 'jour' : 'nuit';
    const memorisees = { ...PREFERENCES_PAR_DEFAUT, ...(lireStockage<Preferences>(CLE_THEME) ?? {}), theme: suivant };
    appliquerPreferences(memorisees);
    ecrireStockage(CLE_THEME, { theme: memorisees.theme, densite: memorisees.densite, taille_police: memorisees.taille_police });
    setTheme(suivant);
  };
  return (
    <button type="button" className={styles.boutonIcone} aria-label={sombre ? 'Passer en mode jour' : 'Passer en mode nuit'} onClick={basculer}>
      {sombre ? <Sun aria-hidden strokeWidth={1.8} width={18} height={18} /> : <Moon aria-hidden strokeWidth={1.8} width={18} height={18} />}
    </button>
  );
}

interface Entree {
  id: string;
  libelle: string;
  href: string;
  icone: ComponentType<{ 'aria-hidden'?: boolean; strokeWidth?: number }>;
}

const PRINCIPALES: Entree[] = [
  { id: 'dossiers', libelle: 'Dossiers', href: '/citoyen/mes-dossiers', icone: FolderOpen },
  { id: 'demarches', libelle: 'Démarches', href: '/citoyen/demarches', icone: FileText },
  { id: 'paiements', libelle: 'Paiements', href: '/citoyen/paiements', icone: CreditCard },
  { id: 'messages', libelle: 'Messages', href: '/citoyen/messages', icone: MessageSquare },
];

function entreeActive(chemin: string): string {
  if (chemin.startsWith('/citoyen/mes-dossiers')) return 'dossiers';
  if (chemin.startsWith('/citoyen/paiements')) return 'paiements';
  if (chemin.startsWith('/citoyen/messages')) return 'messages';
  if (chemin.startsWith('/citoyen/demarches') || /^\/citoyen\/[A-Z0-9]{2,8}\/demarches/.test(chemin)) return 'demarches';
  if (chemin.startsWith('/citoyen/rendez-vous')) return 'rendez-vous';
  if (chemin.startsWith('/citoyen/reservations')) return 'reservations';
  if (chemin.startsWith('/citoyen/reclamations')) return 'reclamations';
  if (chemin.startsWith('/citoyen/signalements')) return 'signalements';
  if (chemin.startsWith('/citoyen/compte/pieces')) return 'pieces';
  if (chemin.startsWith('/citoyen/compte')) return 'compte';
  if (chemin.startsWith('/citoyen/notifications')) return 'notifications';
  if (chemin.startsWith('/citoyen/verification')) return 'verification';
  if (/^\/citoyen\/[A-Z0-9]{2,8}$/.test(chemin)) return 'accueil';
  return '';
}

function MenuCitoyen({ ouvert, id, fermer, codeCollectivite }: { ouvert: boolean; id: string; fermer: () => void; codeCollectivite: string | null }) {
  const { etat, deconnecter } = useSessionUsager();
  const navigate = useNavigate();
  const location = useLocation();
  const actif = entreeActive(location.pathname);
  const secondaires: Entree[] = [
    ...(codeCollectivite ? [{ id: 'accueil', libelle: 'Accueil de la collectivité', href: `/citoyen/${codeCollectivite}`, icone: Home }] : []),
    { id: 'collectivite', libelle: 'Changer de collectivité', href: '/citoyen', icone: Building2 },
    { id: 'rendez-vous', libelle: 'Rendez-vous', href: '/citoyen/rendez-vous', icone: CalendarDays },
    { id: 'reservations', libelle: 'Réservations', href: '/citoyen/reservations', icone: CalendarRange },
    { id: 'reclamations', libelle: 'Réclamations', href: '/citoyen/reclamations', icone: MessageSquareWarning },
    { id: 'signalements', libelle: 'Signalements', href: '/citoyen/signalements', icone: Megaphone },
    { id: 'pieces', libelle: 'Mes pièces', href: '/citoyen/compte/pieces', icone: Paperclip },
    { id: 'notifications', libelle: 'Notifications', href: '/citoyen/notifications', icone: Bell },
    { id: 'verification', libelle: 'Vérifier un document', href: '/citoyen/verification', icone: ShieldCheck },
    { id: 'compte', libelle: 'Mon compte', href: '/citoyen/compte', icone: UserRound },
  ];
  if (!ouvert) return null;
  return (
    <nav id={id} className={styles.menu} aria-label="Menu du portail">
      <ul className={styles.menuListe}>
        {secondaires.map((e) => {
          const Icone = e.icone;
          return (
            <li key={e.id}>
              <Link to={e.href} className={styles.menuEntree} aria-current={e.id === actif ? 'page' : undefined} onClick={fermer}>
                <Icone aria-hidden strokeWidth={1.75} />
                {e.libelle}
              </Link>
            </li>
          );
        })}
        <li>
          {etat === 'connecte' ? (
            <button
              type="button"
              className={styles.menuEntree}
              onClick={() => {
                fermer();
                void deconnecter().then(() => navigate(codeCollectivite ? `/citoyen/${codeCollectivite}` : '/citoyen', { replace: true }));
              }}
            >
              <LogOut aria-hidden strokeWidth={1.75} />
              Se déconnecter
            </button>
          ) : (
            <Link to="/citoyen/connexion" className={styles.menuEntree} onClick={fermer}>
              <LogIn aria-hidden strokeWidth={1.75} />
              Se connecter
            </Link>
          )}
        </li>
      </ul>
      <div className={styles.piedMenu}>
        <span>Portail citoyen</span>
        <IndicateurEnvironnement environnement={import.meta.env.VITE_CC_ENVIRONNEMENT} />
      </div>
    </nav>
  );
}

function Entete({ codeCollectivite, nomCollectivite, sousTitre }: { codeCollectivite: string | null; nomCollectivite: string; sousTitre: string }) {
  const { etat } = useSessionUsager();
  const notifications = useNotificationsUsager();
  const nonLues = notifications.data?.non_lues ?? 0;
  const libelleNotifications = nonLues > 0 ? `Notifications, ${nonLues} non ${nonLues > 1 ? 'lues' : 'lue'}` : 'Notifications';
  return (
    <header className={styles.entete}>
      <Link to={codeCollectivite ? `/citoyen/${codeCollectivite}` : '/citoyen'} className={styles.marque} aria-label={`${nomCollectivite}, accueil du portail citoyen`}>
        <img src="/marque-collectivitesconnect.png" alt="" width={26} height={22} className="cc-logo-jour" />
        <img src="/marque-collectivitesconnect-fond-sombre.png" alt="" width={26} height={22} className="cc-logo-nuit" />
        <span className={styles.marqueTexte}>
          <strong>{nomCollectivite}</strong>
          <small>{sousTitre}</small>
        </span>
      </Link>
      <div className={styles.actionsEntete}>
        {etat === 'connecte' ? (
          <Link to="/citoyen/notifications" className={styles.boutonIcone} aria-label={libelleNotifications}>
            <Bell aria-hidden strokeWidth={1.8} width={18} height={18} />
            {nonLues > 0 ? <span className={styles.point} aria-hidden /> : null}
          </Link>
        ) : null}
        <BasculeTheme />
        <Link to={etat === 'connecte' ? '/citoyen/compte' : '/citoyen/connexion'} className={styles.boutonIcone} aria-label={etat === 'connecte' ? 'Mon compte' : 'Se connecter'}>
          {etat === 'connecte' ? <UserRound aria-hidden strokeWidth={1.8} width={18} height={18} /> : <LogIn aria-hidden strokeWidth={1.8} width={18} height={18} />}
        </Link>
      </div>
    </header>
  );
}

function Coquille() {
  const location = useLocation();
  const [choisie] = useCollectiviteChoisie();
  const collectivites = useCollectivitesPubliques();
  const [menuOuvert, setMenuOuvert] = useState(false);
  const idMenu = useId();
  const enLigne = useEnLigne();
  const nonLus = useNonLusMessages() ?? 0;
  useEffect(() => setMenuOuvert(false), [location.pathname]);
  useEffect(() => {
    if (!menuOuvert) return;
    const echap = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOuvert(false);
    };
    window.addEventListener('keydown', echap);
    return () => window.removeEventListener('keydown', echap);
  }, [menuOuvert]);

  const actif = entreeActive(location.pathname);
  const detail = collectivites.data?.find((c) => c.code === choisie?.code);
  const nom = choisie?.nom ?? 'Portail citoyen';
  const sousTitre = detail ? `${LIBELLES_TYPE_COLLECTIVITE[detail.type] ?? detail.type} · ${detail.province}` : choisie ? 'Portail citoyen' : 'Choisissez votre collectivité';

  return (
    <div className={styles.coquille}>
      <a className="cc-evitement" href="#contenu-principal">
        Aller au contenu
      </a>
      <Entete codeCollectivite={choisie?.code ?? null} nomCollectivite={nom} sousTitre={sousTitre} />
      <nav className={styles.navBasse} aria-label="Navigation du portail citoyen">
        <ul>
          {PRINCIPALES.map((e) => {
            const Icone = e.icone;
            const compteur = e.id === 'messages' ? nonLus : 0;
            return (
              <li key={e.id}>
                <Link to={e.href} className={styles.entreeBasse} aria-current={e.id === actif ? 'page' : undefined} aria-label={compteur > 0 ? `${e.libelle}, ${libelleNonLus(compteur)}` : undefined}>
                  <Icone aria-hidden strokeWidth={1.8} />
                  {e.libelle}
                  {compteur > 0 ? (
                    <span className={styles.pastille} aria-hidden>
                      {compteur > 99 ? '99+' : compteur}
                    </span>
                  ) : null}
                </Link>
              </li>
            );
          })}
          <li>
            <button type="button" className={styles.entreeBasse} aria-expanded={menuOuvert} aria-controls={idMenu} onClick={() => setMenuOuvert((o) => !o)}>
              {menuOuvert ? <X aria-hidden strokeWidth={1.8} /> : <Menu aria-hidden strokeWidth={1.8} />}
              Menu
            </button>
          </li>
        </ul>
      </nav>
      <MenuCitoyen ouvert={menuOuvert} id={idMenu} fermer={() => setMenuOuvert(false)} codeCollectivite={choisie?.code ?? null} />
      <main id="contenu-principal" className={styles.contenu} tabIndex={-1}>
        {!enLigne ? <Bandeau type="horsligne" /> : null}
        <Outlet />
      </main>
      <footer className={styles.pied}>
        <span>{nom}</span>
        <span>Collectivités fictives, données de présentation</span>
        <IndicateurEnvironnement environnement={import.meta.env.VITE_CC_ENVIRONNEMENT} />
      </footer>
    </div>
  );
}

/** Coquille et session usager de la zone /citoyen. */
export function CoquilleCitoyen() {
  return (
    <FournisseurSessionUsager>
      <Coquille />
    </FournisseurSessionUsager>
  );
}
